import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography } from '@mui/joy';

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component to catch React errors and display helpful debug info
 * Prevents the entire app from crashing when a component throws an error
 */
export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null
		};
	}

	static getDerivedStateFromError(): Partial<State> {
		// Update state so the next render will show the fallback UI
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		// Log error details for debugging
		console.error('ErrorBoundary caught an error:', error, errorInfo);

		// Update state with error details
		this.setState({
			error,
			errorInfo
		});

		// You can also log to an error reporting service here
		// Example: logErrorToService(error, errorInfo);
	}

	render() {
		if (this.state.hasError) {
			// Custom fallback UI
			if (this.props.fallback) {
				return this.props.fallback;
			}

			// Default error UI with debugging info
			return (
				<Box
					sx={{
						padding: '2rem',
						backgroundColor: '#fff',
						border: '2px solid #f44336',
						borderRadius: '8px',
						margin: '2rem'
					}}
				>
					<Typography level='h2' sx={{ color: '#f44336', marginBottom: '1rem' }}>
						Something went wrong
					</Typography>

					{this.state.error && (
						<Box sx={{ marginBottom: '1rem' }}>
							<Typography level='body-sm' sx={{ fontWeight: 'bold' }}>
								Error:
							</Typography>
							<Typography
								level='body-sm'
								sx={{
									fontFamily: 'monospace',
									backgroundColor: '#f5f5f5',
									padding: '0.5rem',
									borderRadius: '4px',
									marginTop: '0.5rem'
								}}
							>
								{this.state.error.toString()}
							</Typography>
						</Box>
					)}

					{this.state.errorInfo && (
						<Box>
							<Typography level='body-sm' sx={{ fontWeight: 'bold' }}>
								Component Stack:
							</Typography>
							<Typography
								level='body-sm'
								component='pre'
								sx={{
									fontFamily: 'monospace',
									backgroundColor: '#f5f5f5',
									padding: '0.5rem',
									borderRadius: '4px',
									marginTop: '0.5rem',
									overflow: 'auto',
									maxHeight: '300px'
								}}
							>
								{this.state.errorInfo.componentStack}
							</Typography>
						</Box>
					)}

					<Typography level='body-sm' sx={{ marginTop: '1rem', color: '#666' }}>
						Please check the browser console for more details.
					</Typography>
				</Box>
			);
		}

		return this.props.children;
	}
}
